import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, Mail, MessageCircle, CheckCircle2, HeartHandshake, Phone } from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/lib/site";
import Navbar from "@/components/Navbar";
import { SiteFooter } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const errDetail = (e, f) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : f; };
const CONTACT_EMAIL = "info@motleymentors.com";

const INTERESTS = [["mentor", "I want to become a mentor"], ["mentee", "I'm looking for a mentor"], ["general", "Something else"]];

const Contact = () => {
  const [params] = useSearchParams();
  const site = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", skills: "", experience: "", profile: "", interest: params.get("interest") === "mentee" ? "mentee" : params.get("interest") === "general" ? "general" : "mentor" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const email = site.contact_email || CONTACT_EMAIL;
  const wa = (site.whatsapp || "").replace(/\D/g, "");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/contact", form); setDone(true); }
    catch (err) { toast.error(errDetail(err, "Could not send your message.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
        <aside>
          <p className="font-body text-xs uppercase tracking-[0.2em] text-primary">Contact us</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-slate-900 mt-3 leading-tight" data-testid="contact-title">Let's talk about lighting the way.</h1>
          <p className="font-body text-base md:text-lg text-slate-600 mt-5">Want to mentor with us, find a mentor, or just have a question? Share a few details and our team will get back to you personally.</p>
          <div className="mt-8 space-y-4">
            <a href={`mailto:${email}`} className="flex items-center gap-3 bg-white rounded-2xl border border-slate-200 p-4 hover:border-primary transition-colors" data-testid="contact-email-link">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><Mail className="w-5 h-5 text-primary" /></div>
              <div><p className="font-body text-xs text-slate-400">Email us</p><p className="font-heading font-bold text-slate-900">{email}</p></div>
            </a>
            {wa && (
              <a href={`https://wa.me/91${wa.slice(-10)}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-white rounded-2xl border border-slate-200 p-4 hover:border-primary transition-colors" data-testid="contact-whatsapp-link">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-emerald-600" /></div>
                <div><p className="font-body text-xs text-slate-400">WhatsApp</p><p className="font-heading font-bold text-slate-900">+91 {wa.slice(-10)}</p></div>
              </a>
            )}
          </div>
          <ul className="space-y-3 mt-8">
            {["Mentors set their own session and monthly pricing", "We handle payments and introductions", "Every profile is reviewed personally before going live"].map((p) => (
              <li key={p} className="font-body text-sm text-slate-700 flex items-start gap-2.5"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> {p}</li>
            ))}
          </ul>
        </aside>

        {done ? (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#0B2F55] text-[#F5F1E8] rounded-3xl p-10 self-start" data-testid="contact-success">
            <HeartHandshake className="w-10 h-10 text-sky-300 mb-4" />
            <h2 className="font-heading font-extrabold text-2xl">Thank you, {form.name.split(" ")[0]}!</h2>
            <p className="font-body text-base mt-3 text-[#F5F1E8]/80">We've received your details and will reach out at <span className="font-semibold text-[#F5F1E8]">{form.email}</span>{form.phone ? <> or <span className="font-semibold text-[#F5F1E8]">{form.phone}</span></> : null} shortly. Meanwhile, feel free to write to <a href={`mailto:${email}`} className="underline">{email}</a>.</p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 space-y-5" data-testid="contact-form">
            <div>
              <Label>What brings you here?</Label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="contact-interest">
                {INTERESTS.map(([k, t]) => (
                  <button type="button" key={k} onClick={() => setForm((f) => ({ ...f, interest: k }))} data-testid={`contact-interest-${k}`}
                    className={`font-body text-sm px-3.5 py-1.5 rounded-full border transition-colors ${form.interest === k ? "bg-[#0B2F55] text-white border-[#0B2F55]" : "bg-white text-slate-600 border-slate-200 hover:border-primary"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Full name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5 h-11 rounded-xl" data-testid="contact-name" required /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} className="mt-1.5 h-11 rounded-xl" data-testid="contact-email" required /></div>
            </div>
            <div><Label>Phone number</Label><div className="relative mt-1.5"><Phone className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" /><Input value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" className="h-11 rounded-xl pl-10" data-testid="contact-phone" required /></div></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Skills / areas of expertise</Label><Input value={form.skills} onChange={set("skills")} placeholder="Product management, Career switch, Data science" className="mt-1.5 h-11 rounded-xl" data-testid="contact-skills" /></div>
              <div><Label>Experience</Label><Input value={form.experience} onChange={set("experience")} placeholder="e.g. 12 years · Senior PM at Swiggy" className="mt-1.5 h-11 rounded-xl" data-testid="contact-experience" /></div>
            </div>
            <div><Label>Short profile</Label><Textarea value={form.profile} onChange={set("profile")} rows={5} placeholder={form.interest === "mentor" ? "Who you are, what you've done and who you'd love to mentor…" : "Tell us about yourself and what you're looking for…"} className="mt-1.5 rounded-xl" data-testid="contact-profile" required /></div>
            <Button type="submit" disabled={busy} className="w-full rounded-full h-12 font-heading font-bold text-base" data-testid="contact-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send to Motley Mentors
            </Button>
          </form>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default Contact;
